"use client";

import { useEffect, useState, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import Table from "../../../../components/Table";
import Pagination from "../../../../components/Pagination";
import ReadOnlyInput from "../../../../components/ReadOnlyInput";
import Dropdown from "../../../../components/Dropdown";
import PharmacistSignatureModal from "../../../global/view/PharmacistSignatureModal";
import ActionTile from "../../Chronic/components/ActionTile";
import FeeSummaryCard from "../../Chronic/components/FeeSummaryCard";
import DeliveryOptions from "../../Chronic/components/DeliveryOptions";
import styles from "../../Chronic/css/ChronicScriptView.module.css";
import { fetchScriptDetails, fetchScriptDocumentHistory } from "../../../../api/dispensary/script/processScriptApi";
import { fetchPrescriptionPayment } from "../../../../api/medicalHistory/medicalHistoryApi";
import { useAuth } from "../../../../context/AuthContext";
import { getBranchByCode, getCompany } from "../../../../api/branchManagement]/branchApi";
import { getProfileByPatient } from "../../../../api/profile/create_profile";

export default function ScriptHistoryDetailsView({
  scriptData: propScriptData,
  prescriptionId: propPrescriptionId,
  showTotalItems = false,
  invoiceActionLabel = "View Invoice",
  hideRadios = false,
  hideMedicationLabel = false,
  hidePackageLabel = false,
  hideViewScriptDocument = false,
  deliveryLabelText = "Delivery Label",
  onDeliveryAction,
  onInvoiceSigned,
  centerTopControls = false,
  hidePharmacist = false,
  hidePractitioner = false,
  paymentTypeAsInput = false,
  showPharmacistReadOnly = false,
  showPaymentTypeReadOnly = false,
  showPractitionerReadOnly = false,
  topControlsColumns = 2,
  rowsPerPage = 50,
  scriptNumberLabel = "Script Number",
  usePrescriptionPaymentDelivery = false,
  readOnlyDelivery = false,
  forceHideLabelButtons = false,
  onPreviewDataChange,
  onBeforeInvoiceSign,
  labelConfigBasePath = "/dispensary/label-configuration",
  disableRemoteScriptDetails = false,
  minPreferredDate,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { scriptData: navScriptData } = location.state || {};

  const stored = (() => {
    try {
      const s = sessionStorage.getItem("currentScript");
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  })();

  const [loadedData, setLoadedData] = useState(null);
  const baseData = propScriptData || navScriptData || stored || {};
  const scriptData = { ...(baseData || {}), ...(loadedData || {}) };
  const [deliveryOption, setDeliveryOption] = useState("Collect");
  const [preferredDate, setPreferredDate] = useState("");
  const [preferredTime, setPreferredTime] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureImage, setSignatureImage] = useState(null);
  const [pt, setPt] = useState(scriptData?.paymentType || "");
  const [dispFee, setDispFee] = useState(0);
  const [companyName, setCompanyName] = useState("");
  const [pharmacyRegNumber, setPharmacyRegNumber] = useState("");
  const [patientProfile, setPatientProfile] = useState(null);
  const [scriptDocUrl, setScriptDocUrl] = useState("");
  const [documentCount, setDocumentCount] = useState(0);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [documentHistory, setDocumentHistory] = useState([]);
  const scriptIdNumericRef = useRef(null);
  const lastPreviewRef = useRef(null);

  const isMedicalHistoryScriptDetailsRoute =
    typeof window !== "undefined" &&
    (window.location?.pathname || "").includes(
      "/medical-history/medical-history-details/script-history-details"
    );
  const handlePreferredDateChange = (value) => {
    if (!value) {
      setPreferredDate("");
      return;
    }
    const next = String(value).split("T")[0];
    if (minPreferredDate) {
      const min = String(minPreferredDate).split("T")[0];
      if (next < min) {
        return;
      }
    }
    setPreferredDate(next);
  };

  useEffect(() => {
    const bc = user?.branchcode;
    if (!bc) return;

    let alive = true;
    (async () => {
      try {
        const b = await getBranchByCode(bc);
        if (!alive || !b) return;
        const v = parseFloat(b.dispensingfee);
        setDispFee(Number.isFinite(v) ? v : 0);
      } catch (e) {
        console.error("Failed to fetch branch dispensing fee", e);
      }
    })();
    return () => { alive = false; };
  }, [user?.branchcode]);


  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await getCompany();
        if (!alive || !data) return;
        setCompanyName(data.companyname || "");
        setPharmacyRegNumber(data.sapcpharmacyregnumber || "");
      } catch (e) {
        // silent
      }
    })();
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    let alive = true;
    (async () => {
 try {
        const bc = loadedData?.script?.branchcode
          || baseData?.script?.branchcode
          || baseData?.pharmacist?.branch
          || user?.branchcode;
        if (!bc) return;
        const b = await getBranchByCode(bc);
        if (!alive || !b) return;
        const v = parseFloat(b.dispensingfee);
        setDispFee(Number.isFinite(v) ? v : 0);
      } catch (e) {
        // silent
      }
    })();
    return () => { alive = false; };
  }, [loadedData?.script?.branchcode, baseData?.script?.branchcode, baseData?.pharmacist?.branch, user?.branchcode]);


  useEffect(() => {
    const pid = baseData?.patientId || scriptData?.patientId;
    if (!pid) return;

    let alive = true;
    (async () => {
      try {
        const prof = await getProfileByPatient(pid);
        if (!alive || !prof) return;
        const main = prof.main || {};
        const mm = prof.mainmember || {};
        const requested = prof.patient || {};
        const profile = prof.profile || {};
        const profileNumber = profile.profilenumber || mm.profilenumber || baseData?.profileNo;
        const addrSource = requested.homeaddress || main.homeaddress;
        const addr = addrSource ? [String(addrSource)] : [];

        const phoneVal = requested.contactnumber || main.contactnumber || "";
        const medAidNumber =
          mm.medicalaidnumber ||
          mm.medicalaidno ||
          mm.medicalAidNumber ||
          mm.medicalAidNo ||
          mm.medicalaidprovider ||
          baseData?.medicalAidNumber ||
          "";
        setPatientProfile({
          profileNo: profileNumber || baseData?.profileNo || "",
          addressLines: addr,
          phone: phoneVal,
          medicalAid: medAidNumber,
        });
        setLoadedData((prev) => {
          const current = prev || baseData || {};
          const existingAddress =
            Array.isArray(current.addressLines) && current.addressLines.length
              ? current.addressLines
              : [];
          const existingPhone =
            current.phone && String(current.phone).trim() ? current.phone : "";

          return {
            ...current,
            profileNo: profileNumber || current.profileNo,
            addressLines: existingAddress.length ? existingAddress : addr,
            phone: existingPhone || phoneVal,
            medicalAidNumber: medAidNumber || current.medicalAidNumber,
          };
        });
      } catch (e) {
        // keep existing
      }
    })();
    return () => { alive = false; };
  }, [baseData?.patientId, scriptData?.patientId]);

  useEffect(() => {
    if (disableRemoteScriptDetails) return;
    const firstNumeric = (val) => {
      if (val == null) return null;
      const n = Number(val);
      return Number.isFinite(n) ? n : null;
    };
    const idNumeric = firstNumeric(baseData?.id)
      ?? firstNumeric(baseData?.scriptId)
      ?? firstNumeric(baseData?.scriptNo)
      ?? firstNumeric(baseData?.scriptNumber);
    if (!idNumeric) return;

    scriptIdNumericRef.current = idNumeric;

    let alive = true;
    (async () => {
      try {
        const data = await fetchScriptDetails(String(idNumeric));
        if (!alive || !data) return;

        const pharmacist = data.pharmacist
          ? `${data.pharmacist.firstname || ""} ${data.pharmacist.surname || ""}`.trim()
          : "";
        const practitioner = data.practitioner
          ? `${data.practitioner.title ? data.practitioner.title + " " : ""}${data.practitioner.firstname || ""} ${data.practitioner.surname || ""}`.trim()
          : "";

        const patientFromDetails = data.patient
          ? `${data.patient.title ? data.patient.title + " " : ""}${data.patient.firstname || ""} ${data.patient.surname || ""}`.trim()
          : "";
        const profileFromDetails = data.patient?.profileNo || data.patient?.profileno || null;

        const addrFromDetails = [];
        const p = data.patient || {};
        if (p.homeStreet) addrFromDetails.push(p.homeStreet);
        if (p.homeSuburb) addrFromDetails.push(p.homeSuburb);
        if (p.homeCity) addrFromDetails.push(p.homeCity);
        if (p.homePostalCode) addrFromDetails.push(p.homePostalCode);

        const phoneFromDetails =
          p.cell ||
          p.cellphone ||
          p.cellphoneNo ||
          p.mobile ||
          p.phone ||
          p.telephone ||
          null;

        const medicalAidNumberFromDetails = data.medicalAid?.number || null;

        setLoadedData((prev) => {
          const current = prev || baseData || {};
          const existingAddress =
            Array.isArray(current.addressLines) && current.addressLines.length
              ? current.addressLines
              : [];
          const existingPhone =
            current.phone && String(current.phone).trim() ? current.phone : "";

          const isAmendFlow = !!current.amendFlow;

          const merged = {
            ...current,
            scriptNumber: String(current.scriptNumber || data.script?.id || idNumeric),
            date: data.script?.date || current.date || "",
            paymentType: data.script?.paymentType || current.paymentType || "",
            practitioner: practitioner || current.practitioner || "",
            pharmacist: pharmacist || current.pharmacist || "",
            drugs: isAmendFlow
              ? (Array.isArray(current.drugs) ? current.drugs : (Array.isArray(data.drugs) ? data.drugs : []))
              : (Array.isArray(data.drugs) ? data.drugs : (Array.isArray(current.drugs) ? current.drugs : [])),
            scriptTypeRaw: data.script?.type || current.scriptTypeRaw || "",
            processScriptNumber: data.script?.processscriptnumber || current.processScriptNumber || "",
            clinicReference: data.script?.clinicreference || current.clinicReference || "",
            otcReference: data.script?.otcreference || current.otcReference || "",
            practiceNumber: data.practitioner?.practiceNumber || current.practiceNumber || "",
            patient: patientFromDetails || current.patient || "",
            profileNo: profileFromDetails || current.profileNo,
            addressLines: existingAddress.length ? existingAddress : addrFromDetails,
            phone: existingPhone || phoneFromDetails || "",
            medicalAidNumber: medicalAidNumberFromDetails || current.medicalAidNumber || "",
          };
          if (merged.paymentType) setPt(merged.paymentType);
          return merged;
        });
        setScriptDocUrl(data.script?.docurl || "");
        setDocumentCount(Number(data.script?.documentCount || 0));
      } catch (e) {
        console.error("Failed to fetch script details", e);
      }
    })();
    return () => { alive = false; };


  }, [baseData?.scriptNumber, baseData?.scriptNo, baseData?.scriptId, baseData?.id]);

  useEffect(() => {
    if (!usePrescriptionPaymentDelivery) return;
    const firstNumeric = (val) => {
      if (val == null) return null;
      const n = Number(val);
      return Number.isFinite(n) ? n : null;
    };
    const idNumeric = firstNumeric(baseData?.id)
      ?? firstNumeric(baseData?.scriptId)
      ?? firstNumeric(baseData?.scriptNo)
      ?? firstNumeric(baseData?.scriptNumber);
    if (!idNumeric) return;

    let alive = true;
    (async () => {
      try {
        const payment = await fetchPrescriptionPayment(String(idNumeric));
        if (!alive || !payment) return;

        if (payment.deliveryOption) setDeliveryOption(payment.deliveryOption);
        if (payment.preferredDate) setPreferredDate(payment.preferredDate);
        if (payment.preferredTime) setPreferredTime(payment.preferredTime);

      } catch (e) {
        console.error("Failed to fetch prescription payment", e);
      }
    })();
    return () => { alive = false; };
  }, [usePrescriptionPaymentDelivery, baseData?.scriptNumber, baseData?.scriptNo, baseData?.scriptId, baseData?.id]);

  const scriptNumber = propScriptData?.scriptNumber || scriptData?.scriptNumber || scriptData?.scriptNo || "SCR001-1";
  const pharmacist = scriptData?.pharmacist || "Dr. Jane Doe";
  const paymentType = pt || "Cash";
  const practitioner = scriptData?.practitioner || "Dr. John Brown";

  const patientLabel = scriptData?.patient || baseData?.patient || "";
  const profileLabelFromData = scriptData?.profileNo || baseData?.profileNo || "";
  const profileFromLabelMatch = /\(([^()]+)\)\s*$/.exec(patientLabel || "");
  const profileLabel = profileLabelFromData || (profileFromLabelMatch ? profileFromLabelMatch[1] : "");
  const scriptAddressLines = scriptData?.addressLines || baseData?.addressLines || [];
  const profileAddressLines = patientProfile && Array.isArray(patientProfile.addressLines) ? patientProfile.addressLines : [];
  const scriptPhone = scriptData?.phone || baseData?.phone || "";
  const profilePhone = (patientProfile?.phone && String(patientProfile.phone).trim()) || "";

  const patientInfo = {
    name: patientLabel || "Patient",
    patient: patientLabel || "",
    profile: profileLabel || (patientProfile?.profileNo || ""),
    address: (Array.isArray(scriptAddressLines) && scriptAddressLines.length ? scriptAddressLines : profileAddressLines),
    phone: scriptPhone || profilePhone || "",
    medicalAid: (patientProfile?.medicalAid && String(patientProfile.medicalAid).trim()) || scriptData?.medicalAidNumber || baseData?.medicalAidNumber || "",
  };

  const toMoneyString = (value, prefix = "R") => {
    if (value == null) return "";
    if (typeof value === "number") return `${prefix}${value.toFixed(2)}`;
    return String(value);
  };

  const tableData = Array.isArray(scriptData?.drugs) && scriptData.drugs.length > 0
    ? scriptData.drugs.map((d) => ({
        drugName: d.drugName || "",
        packSize: d.packSize || "",
        icd10: d.icd10 || "",
        strength: d.strength || "",
        qty: d.quantity || d.qty || 0,
        nappiCode: d.nappiCode || "",
        instructions: d.instructions || "",
        costPrice: d.costPrice || "",
      }))
    : [
        {
          drugName: "Paracetamol",
          packSize: "100 tablets",
          icd10: "M54.9",
          strength: "500mg",
          qty: 50,
          nappiCode: "NAP123456",
          instructions: "Take 1 tablet every 6 hours as needed",
          costPrice: 25.0,
        },
      ];

  const parseMoney = (v) => {
    if (v == null) return 0;
    if (typeof v === "string") {
      const cleaned = v.replace(/^[^\d.-]*/, "");
      const n = parseFloat(cleaned);
      return Number.isFinite(n) ? n : 0;
    }
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };

  const dispensingFee = dispFee;
  const sumQty = (arr) => arr.reduce((s, r) => s + (parseFloat(r.qty) || 0), 0);
  const st = (scriptData?.scriptType || "PR").toUpperCase();
  const isMedicalAid = paymentType === "Medical Aid";
  const pricedSum = tableData.reduce((sum, r) => {
    const cp = parseMoney(r.costPrice);
    const qty = parseFloat(r.qty) || 0;
    if (!Number.isFinite(cp) || cp <= 0 || qty <= 0) return sum;
    const base = cp * qty;
    if (isMedicalAid) return sum + base;
    if (st === "PR") return sum + base * 1.34;
    if (st === "PR26") {
      if (base < 100) return sum + (base + 26);
      return sum + base * 1.26;
    }
    return sum + base;
  }, 0);
  const grossAmount = (pricedSum + dispensingFee).toFixed(2);

  const itemsCount = Array.isArray(scriptData?.drugs)
    ? scriptData.drugs.reduce((sum, d) => sum + (parseFloat(d.quantity ?? d.qty) || 0), 0)
    : sumQty(tableData);

  const scriptTypeRaw = String(scriptData?.scriptTypeRaw || scriptData?.scriptType || "").toLowerCase();

  let scriptRef = "";
  if (scriptData?.otcReference) {
    scriptRef = scriptData.otcReference;
  } else if (scriptTypeRaw === "process") {
    scriptRef = scriptData?.processScriptNumber || scriptData?.scriptNumber || "";
  } else if (scriptTypeRaw === "clinic") {
    scriptRef = scriptData?.clinicReference || scriptData?.scriptNumber || "";
  } else {
    scriptRef = scriptData?.scriptNumber || "";
  }

  const prescription = {
    title: "CERTIFIED COPY OF DOCTORS PRESCRIPTION",
    lines: Array.isArray(scriptData?.drugs)
      ? scriptData.drugs.map((d) => ({
          code: d.nappiCode || d.code || "",
          drug: d.drugName || "",
          qty: String(d.quantity ?? d.qty ?? ""),
          side: d.side || "",
          icd10: d.icd10 || "",
          price: toMoneyString(d.costPrice, "R "),
          instructions: d.instructions ? [d.instructions] : [],
        }))
      : [],
    pharmacy: companyName || scriptData?.pharmacy || "",
    registration: pharmacyRegNumber || scriptData?.pharmacyRegistration || "",
    copyCost: scriptData?.copyCost ? toMoneyString(scriptData.copyCost, "R ") : "",
    total: grossAmount ? `R ${grossAmount}` : (scriptData?.total ? toMoneyString(scriptData.total, "R") : ""),
    doctorName: scriptData?.practitioner || baseData?.practitioner || "",
    practiceNumber: scriptData?.practiceNumber || "",
    scriptRef,
    itemsCount,
  };

  const previewKey = useMemo(
    () => JSON.stringify({ patientInfo, prescription, deliveryOption, preferredDate, preferredTime, dispensingFee, grossAmount }),
    [patientInfo, prescription, deliveryOption, preferredDate, preferredTime, dispensingFee, grossAmount]
  );

  useEffect(() => {
    if (typeof onPreviewDataChange !== "function") return;

    if (lastPreviewRef.current === previewKey) return;
    lastPreviewRef.current = previewKey;
    onPreviewDataChange({
      patientInfo,
      prescription,
      paymentMeta: {
        deliveryOption,
        preferredDate,
        preferredTime,
        dispensingFee,
        grossAmount,
      },
    });
  }, [onPreviewDataChange, previewKey, patientInfo, prescription, deliveryOption, preferredDate, preferredTime, dispensingFee, grossAmount]);

  const totalPages = Math.ceil(tableData.length / rowsPerPage);
  const paginatedData = tableData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  const columns = [
    { header: "Drug Name", accessor: "drugName" },
    { header: "Pack Size", accessor: "packSize" },
    { header: "ICD-10", accessor: "icd10" },
    { header: "Strength", accessor: "strength" },
    { header: "Qty", accessor: "qty" },
    { header: "Nappi Code", accessor: "nappiCode" },
  ];

  const handleDeliveryLabel = () => console.log("Generate Delivery Label");
  const handlePackageAndLabel = () => navigate("/customer/dispensary/package-script");
  const handleShowDocumentHistory = async () => {
    const idNumeric = scriptIdNumericRef.current;
    if (!idNumeric) {
      return;
    }
    setHistoryError("");
    setHistoryLoading(true);
    try {
      const docs = await fetchScriptDocumentHistory(String(idNumeric));
      setDocumentHistory(Array.isArray(docs) ? docs : []);
      setHistoryModalOpen(true);
    } catch (e) {
      console.error("Failed to fetch script document history", e);
      setHistoryError("Failed to load document history.");
    } finally {
      setHistoryLoading(false);
    }
  };
  const handleViewScriptDocument = () => {
    const url =
      scriptDocUrl ||
      scriptData?.scriptDocUrl ||
      baseData?.scriptDocUrl ||
      "";

    if (url && typeof window !== "undefined") {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      console.log("No script document URL available for this prescription");
    }
  };
  const handleViewInvoice = () => {
    if (isMedicalHistoryScriptDetailsRoute) {
      return;
    }
    if (onBeforeInvoiceSign) {
      const canProceed = onBeforeInvoiceSign();
      if (canProceed === false) {
        return;
      }
    }
    setShowSignatureModal(true);
  };
  const handleMedicationLabel = async () => {
    const icd10Codes = scriptData?.drugs?.map(drug => drug.icd10).filter(Boolean) || [];
    const prescriptionIdToUse =
      propPrescriptionId ||
      scriptData?.id ||
      scriptData?.scriptId ||
      scriptData?.scriptNo ||
      scriptData?.scriptNumber;
    const scriptNumberToUse = String(
      scriptData?.scriptNumber ||
      scriptData?.scriptNo ||
      scriptData?.id ||
      scriptData?.scriptId ||
      ""
    );
    const { searchInventory } = await import("../../../../api/dispensary/script/LabelApi.js");
    const labelDataPromises = scriptData?.drugs?.map(async (drug, index) => {
      const inventoryResponse = await searchInventory(drug.drugName);
      const inventoryItem =
        inventoryResponse.success && Array.isArray(inventoryResponse.data) && inventoryResponse.data.length > 0
          ? inventoryResponse.data[0]
          : null;
      const actualBarcode = inventoryItem?.barcode || "";

      console.log("Barcode for medication", drug.drugName, ":", actualBarcode);

      return {
        id: `med-${index}`,
        data: {
          patientName: scriptData?.patient || patientInfo?.name || "",
          medication: drug.drugName || "",
          quantity: drug.quantity || drug.qty || "",
          prescriber: scriptData?.practitioner || "",
          practitioner: scriptData?.practitioner || "",
          rxNumber: scriptNumberToUse,
          directions: drug.instructions || "",
          packSize: drug.packSize || "",
          icd10: icd10Codes[index] || drug.icd10 || "",
          barcode: actualBarcode,
          date: new Date().toISOString().split("T")[0],
          pharmacistName: scriptData?.pharmacist || "",
        },
        isDrug: true,
      };
    }) || [];

    const labelData = await Promise.all(labelDataPromises);
    const medicationsArray = scriptData?.drugs?.map((drug, index) => ({
      id: `med-${index}`,
      name: drug.drugName || "",
      quantity: drug.quantity || drug.qty || "",
      packSize: drug.packSize || "",
      barcode: drug.nappiCode || "",
      icd10Codes: icd10Codes[index]
        ? [icd10Codes[index]]
        : (drug.icd10 ? [drug.icd10] : []),
    })) || [];

    const targetUrl = prescriptionIdToUse
      ? `${labelConfigBasePath}?prescriptionId=${prescriptionIdToUse}&icd10Codes=${icd10Codes.join(",")}`
      : `${labelConfigBasePath}?icd10Codes=${icd10Codes.join(",")}`;

    // Navigate with complete data in state
    navigate(targetUrl, {
      state: {
        labelData,
        medications: medicationsArray,
        scriptNumber: scriptNumberToUse, // Pass script number
        patientName: scriptData?.patient || patientInfo?.name || "", // Pass patient name
        totals: {
          total: grossAmount
            ? `R ${grossAmount}`
            : (scriptData?.total ? toMoneyString(scriptData.total, "R") : ""),
        }, // Pass total price
        patientInfo: {
          name: scriptData?.patient || patientInfo?.name || "",
          practitioner: scriptData?.practitioner || "",
          pharmacist: scriptData?.pharmacist || "",
        },
      },
    });
  };
  const handleSignatureNext = (sig) => {
    setSignatureImage(sig);
    setShowSignatureModal(false);
    if (onInvoiceSigned) {
      onInvoiceSigned(sig);
    }
  };
  const isDeliver = deliveryOption === "Deliver";
  return (
    <div className={styles.page}>
      <Navbar />
      <main className={styles.main}>
        <div
          className={styles.controlsRow}
          style={centerTopControls ? { gridTemplateColumns: `repeat(${topControlsColumns}, 220px)`, justifyContent: "center" } : undefined}
        >
          <ReadOnlyInput label={scriptNumberLabel} value={scriptNumber} />
          {showTotalItems && (
            <ReadOnlyInput
              label="Total Items"
              value={typeof scriptData?.totalItems === "number" ? scriptData.totalItems : sumQty(tableData)}
            />
          )}
          {!hidePharmacist && (
            <ReadOnlyInput label="Pharmacist" value={pharmacist} />
          )}
          <div className={styles.field}>
            <label className={styles.matchLabel}>Payment Type</label>
            <ReadOnlyInput label="" value={paymentType} />
          </div>
          {!hidePractitioner && (
            <ReadOnlyInput label="Practitioner" value={practitioner} />
          )}
        </div>

        <div className={styles.tableCard}>
          <Table columns={columns} data={paginatedData} />
        </div>

        <br />

        <div className={styles.midRow}>
          <div className={styles.tilesMiddle}>
            <div className={styles.tilesCol}>
              {!hideViewScriptDocument && (
                <ActionTile title="Script" rightLinkLabel="View" onRightLinkClick={handleViewScriptDocument} />
              )}
              {documentCount > 0 && (
                <div className={styles.docSummary}>
                  <span>Documents: {documentCount}</span>
                  <button
                    type="button"
                    onClick={handleShowDocumentHistory}
                    className={styles.docSummaryButton}
                  >
                    {historyLoading ? "Loading..." : "Show details"}
                  </button>
                  {historyError && (
                    <div className={styles.docSummaryError}>{historyError}</div>
                  )}
                </div>
              )}
              {invoiceActionLabel && (
                <ActionTile
                  title={invoiceActionLabel === "Sign Invoice" ? "Sign Invoice" : "Invoice"}
                  onClick={handleViewInvoice}
                />
              )}
            </div>
          </div>

          <div className={styles.optionsRight}>
            {!hideRadios &&
              (readOnlyDelivery ? (
                <DeliveryOptions
                  mode={deliveryOption}
                  onModeChange={() => {}}
                  preferredDate={preferredDate}
                  onDateChange={() => {}}
                  preferredTime={preferredTime}
                  onTimeChange={() => {}}
                  timeSlots={preferredTime ? [preferredTime] : undefined}
                  readOnly
                  hideUnselected
                  minDate={minPreferredDate}
                />
              ) : (
                <DeliveryOptions
                  mode={deliveryOption}
                  onModeChange={setDeliveryOption}
                  preferredDate={preferredDate}
                  onDateChange={handlePreferredDateChange}
                  preferredTime={preferredTime}
                  onTimeChange={setPreferredTime}
                  hideUnselected={false}
                  minDate={minPreferredDate}
                />
              ))}

          </div>

          <div className={styles.feesLeft}>
            <FeeSummaryCard dispensingFee={`R ${dispensingFee}`} gross={`R ${grossAmount}`} />
          </div>
        </div>

        {!forceHideLabelButtons && (
          <div className={styles.bottomButtons}>
            {!hideMedicationLabel && (
              <button className={styles.btnPrimary} onClick={handleMedicationLabel}>
                Medication Label
              </button>
            )}
            <button
              className={styles.btnPrimary}
              onClick={() => {
                if (typeof onDeliveryAction === "function") onDeliveryAction();
                else handleDeliveryLabel();
              }}
            >
              {deliveryLabelText}
            </button>
            {!hidePackageLabel && !isDeliver && (
              <button className={styles.btnPrimary} onClick={handlePackageAndLabel}>
                Package and Label
              </button>
            )}
          </div>
        )}

        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
          />
        )}
      </main>

      <Footer />

      <PharmacistSignatureModal
        isOpen={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        onNext={handleSignatureNext}
        initialSignature={signatureImage}
        previewProps={{ patientInfo, prescription }}
      />

      {historyModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3>Script Document History</h3>
            <button
              type="button"
              onClick={() => setHistoryModalOpen(false)}
              className={styles.modalCloseButton}
            >
              ×
            </button>
            {documentHistory.length === 0 ? (
              <p>No documents found for this prescription.</p>
            ) : (
              <Table
                columns={[
                  { header: "Uploaded At", accessor: "uploadedAt" },
                  { header: "Uploaded By", accessor: "uploadedBy" },
                  { header: "Context", accessor: "context" },
                  { header: "Active", accessor: "isActive" },
                  { header: "File Name", accessor: "originalName" },
                  { header: "Actions", accessor: "actions" },
                ]}
                data={documentHistory.map((d) => ({
                  uploadedAt: d.uploadedAt ? new Date(d.uploadedAt).toLocaleString() : "",
                  uploadedBy: d.uploadedBy || "",
                  context: d.context || "",
                  isActive: d.isActive ? "Yes" : "No",
                  originalName: d.originalName || "",
                  actions: (
                    <button
                      type="button"
                      onClick={() => {
                        if (d.viewUrl && typeof window !== "undefined") {
                          window.open(d.viewUrl, "_blank", "noopener,noreferrer");
                        }
                      }}
                      className={styles.btnPrimarySmall}
                    >
                      View
                    </button>
                  ),
                }))}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

